import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowLeft, MessageSquare, CheckCircle, XCircle } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import SEO from '../components/SEO';

const SmsConsentPage = () => {
  const { language } = useLanguage();

  const content = {
    es: {
      title: 'Consentimiento SMS',
      lastUpdated: 'Última actualización: Marzo 2025',
      backHome: 'Volver al inicio',
      intro: {
        title: 'Política de Mensajes SMS',
        content: 'Esta página describe cómo AJ Code Labs ("nosotros", "la Empresa") utiliza mensajes de texto SMS para comunicarse con nuestros usuarios y clientes, y cómo puede administrar sus preferencias de comunicación.'
      },
      sections: [
        {
          title: '1. Consentimiento para Recibir SMS',
          content: `Al proporcionar su número de teléfono y optar por recibir mensajes SMS de AJ Code Labs, usted consiente expresamente recibir mensajes de texto de nuestra parte.

**Cómo puede dar su consentimiento:**
• Marcando la casilla de consentimiento SMS en nuestros formularios
• Enviando un mensaje de texto con la palabra "SUSCRIBIR" a nuestro número
• Aceptando recibir notificaciones SMS en la configuración de su cuenta
• Proporcionando su número de teléfono durante el proceso de registro en una aplicación

**Su consentimiento es completamente voluntario** y no es condición para la compra de ningún producto o servicio.`
        },
        {
          title: '2. Tipos de Mensajes que Puede Recibir',
          content: `Dependiendo de sus preferencias y los productos que utilice, puede recibir:

**Mensajes Transaccionales:**
• Confirmaciones de citas o servicios
• Actualizaciones del estado de su proyecto
• Códigos de verificación y seguridad
• Notificaciones de pago y facturación
• Alertas importantes de su cuenta

**Mensajes Promocionales (solo con consentimiento adicional):**
• Ofertas especiales y descuentos
• Anuncios de nuevos servicios o productos
• Invitaciones a eventos
• Contenido educativo y recursos

**Mensajes Específicos de Aplicaciones:**
• Notificaciones y alertas propias de cada aplicación
• Recordatorios programados por el usuario
• Actualizaciones de funcionalidades`
        },
        {
          title: '3. Frecuencia de Mensajes',
          content: `**Mensajes Transaccionales:**
La frecuencia varía según su actividad y el uso de nuestros servicios. Estos mensajes se envían solo cuando son necesarios para el funcionamiento del servicio.

**Mensajes Promocionales:**
• Máximo 4 mensajes por mes
• Nunca enviamos mensajes promocionales en horarios inconvenientes (antes de las 9 AM o después de las 9 PM en su zona horaria)

**Mensajes de Aplicaciones:**
La frecuencia depende de la configuración específica de cada aplicación y las preferencias del usuario dentro de la misma.`
        },
        {
          title: '4. Costos y Cargos',
          content: `**Importante:** Pueden aplicarse tarifas estándar de mensajes y datos según su plan de telefonía móvil.

• AJ Code Labs no cobra por enviar mensajes SMS
• Los cargos de su operador de telefonía móvil pueden variar
• Consulte con su proveedor de servicios móviles para conocer sus tarifas
• No somos responsables de los cargos aplicados por su operador`
        },
        {
          title: '5. Cómo Cancelar su Suscripción',
          content: `Puede dejar de recibir mensajes SMS en cualquier momento utilizando cualquiera de estos métodos:

**Método Rápido - Por SMS:**
Responda "STOP", "CANCELAR", "BAJA" o "UNSUBSCRIBE" a cualquier mensaje que reciba de nosotros.

**Por Email:**
Envíe un correo a contacto@ajcodelabs.ai solicitando la cancelación de SMS.

**En su Cuenta:**
Actualice sus preferencias de comunicación en la configuración de su cuenta o aplicación.

**Confirmación:**
Recibirá un mensaje confirmando su cancelación. Después de esto, no recibirá más mensajes SMS de nuestra parte, excepto aquellos estrictamente necesarios para el servicio (como códigos de seguridad si los solicita).`
        },
        {
          title: '6. Cómo Obtener Ayuda',
          content: `Si tiene preguntas sobre nuestros mensajes SMS:

**Por SMS:**
Responda "AYUDA" o "HELP" a cualquier mensaje nuestro.

**Por Email:**
Escriba a contacto@ajcodelabs.ai

**Responderemos dentro de 24-48 horas hábiles.**`
        },
        {
          title: '7. Privacidad de su Número de Teléfono',
          content: `Su privacidad es importante para nosotros:

**Protección de Datos:**
• Su número de teléfono se almacena de forma segura
• Nunca vendemos ni compartimos su número con terceros para fines de marketing
• Solo compartimos su número con proveedores de servicios SMS necesarios para la entrega de mensajes

**Proveedores de Servicio:**
Utilizamos proveedores de SMS confiables y seguros que cumplen con estándares de la industria para la protección de datos.

Para más información sobre cómo protegemos sus datos, consulte nuestra Política de Privacidad.`
        },
        {
          title: '8. Consentimiento en Aplicaciones',
          content: `Algunas de nuestras aplicaciones pueden solicitar consentimiento SMS específico:

**Aplicaciones con Notificaciones SMS:**
Cada aplicación explicará claramente qué tipo de mensajes enviará y con qué propósito antes de solicitar su consentimiento.

**Gestión por Aplicación:**
Puede gestionar las preferencias de SMS de cada aplicación de forma independiente en la configuración de la misma.

**Retiro del Consentimiento:**
Retirar el consentimiento de SMS en una aplicación no afecta sus preferencias en otras aplicaciones o servicios.`
        },
        {
          title: '9. Operadores Móviles Compatibles',
          content: `Nuestro servicio de SMS es compatible con los principales operadores de Estados Unidos, Latinoamérica y Europa, incluyendo pero no limitado a:

**Estados Unidos:** AT&T, Verizon, T-Mobile, Sprint, US Cellular
**México:** Telcel, AT&T México, Movistar
**Ecuador:** Claro, Movistar, CNT
**Colombia:** Claro, Movistar, Tigo
**España:** Movistar, Vodafone, Orange
**Reino Unido:** EE, Vodafone, Three, O2

Los operadores no son responsables de mensajes retrasados o no entregados.`
        },
        {
          title: '10. Cambios a esta Política',
          content: `Podemos actualizar esta política de consentimiento SMS periódicamente:

• Los cambios se publicarán en esta página
• Si realiza cambios significativos, le notificaremos por SMS o email
• Su uso continuado de nuestros servicios después de los cambios implica aceptación

Le recomendamos revisar esta página periódicamente.`
        },
        {
          title: '11. Contacto',
          content: `Para cualquier pregunta sobre esta política o nuestras prácticas de SMS:

**AJ Code Labs**
Email: contacto@ajcodelabs.ai

**Respuestas por SMS:**
• STOP - Cancelar todos los mensajes
• HELP - Obtener ayuda
• INFO - Información sobre el programa

Oficinas:
• Miami, FL - Estados Unidos
• Quito - Ecuador
• Londres - Reino Unido`
        }
      ],
      quickActions: {
        title: 'Acciones Rápidas por SMS',
        actions: [
          { command: 'STOP', description: 'Cancelar todos los mensajes SMS', type: 'cancel' },
          { command: 'HELP', description: 'Obtener ayuda sobre el programa SMS', type: 'help' },
          { command: 'INFO', description: 'Recibir información del programa', type: 'info' },
        ]
      }
    },
    en: {
      title: 'SMS Consent',
      lastUpdated: 'Last updated: March 2025',
      backHome: 'Back to home',
      intro: {
        title: 'SMS Messaging Policy',
        content: 'This page describes how AJ Code Labs ("we", "the Company") uses SMS text messages to communicate with our users and customers, and how you can manage your communication preferences.'
      },
      sections: [
        {
          title: '1. Consent to Receive SMS',
          content: `By providing your phone number and opting to receive SMS messages from AJ Code Labs, you expressly consent to receive text messages from us.

**How you can give consent:**
• Checking the SMS consent box on our forms
• Sending a text message with the word "SUBSCRIBE" to our number
• Accepting to receive SMS notifications in your account settings
• Providing your phone number during the registration process in an application

**Your consent is completely voluntary** and is not a condition for purchasing any product or service.`
        },
        {
          title: '2. Types of Messages You May Receive',
          content: `Depending on your preferences and the products you use, you may receive:

**Transactional Messages:**
• Appointment or service confirmations
• Project status updates
• Verification and security codes
• Payment and billing notifications
• Important account alerts

**Promotional Messages (only with additional consent):**
• Special offers and discounts
• Announcements of new services or products
• Event invitations
• Educational content and resources

**Application-Specific Messages:**
• Notifications and alerts specific to each application
• User-scheduled reminders
• Feature updates`
        },
        {
          title: '3. Message Frequency',
          content: `**Transactional Messages:**
Frequency varies based on your activity and use of our services. These messages are sent only when necessary for service operation.

**Promotional Messages:**
• Maximum 4 messages per month
• We never send promotional messages at inconvenient times (before 9 AM or after 9 PM in your time zone)

**Application Messages:**
Frequency depends on each application's specific settings and user preferences within it.`
        },
        {
          title: '4. Costs and Charges',
          content: `**Important:** Standard message and data rates may apply according to your mobile phone plan.

• AJ Code Labs does not charge for sending SMS messages
• Your mobile carrier charges may vary
• Check with your mobile service provider to know their rates
• We are not responsible for charges applied by your carrier`
        },
        {
          title: '5. How to Unsubscribe',
          content: `You can stop receiving SMS messages at any time using any of these methods:

**Quick Method - By SMS:**
Reply "STOP", "CANCEL", "QUIT" or "UNSUBSCRIBE" to any message you receive from us.

**By Email:**
Send an email to contacto@ajcodelabs.ai requesting SMS cancellation.

**In Your Account:**
Update your communication preferences in your account or application settings.

**Confirmation:**
You will receive a message confirming your cancellation. After this, you will not receive any more SMS messages from us, except those strictly necessary for the service (such as security codes if you request them).`
        },
        {
          title: '6. How to Get Help',
          content: `If you have questions about our SMS messages:

**By SMS:**
Reply "HELP" or "AYUDA" to any of our messages.

**By Email:**
Write to contacto@ajcodelabs.ai

**We will respond within 24-48 business hours.**`
        },
        {
          title: '7. Privacy of Your Phone Number',
          content: `Your privacy is important to us:

**Data Protection:**
• Your phone number is stored securely
• We never sell or share your number with third parties for marketing purposes
• We only share your number with SMS service providers necessary for message delivery

**Service Providers:**
We use reliable and secure SMS providers that comply with industry standards for data protection.

For more information on how we protect your data, see our Privacy Policy.`
        },
        {
          title: '8. Consent in Applications',
          content: `Some of our applications may request specific SMS consent:

**Applications with SMS Notifications:**
Each application will clearly explain what type of messages it will send and for what purpose before requesting your consent.

**Management by Application:**
You can manage SMS preferences for each application independently in its settings.

**Withdrawal of Consent:**
Withdrawing SMS consent in one application does not affect your preferences in other applications or services.`
        },
        {
          title: '9. Compatible Mobile Carriers',
          content: `Our SMS service is compatible with major carriers in the United States, Latin America, and Europe, including but not limited to:

**United States:** AT&T, Verizon, T-Mobile, Sprint, US Cellular
**Mexico:** Telcel, AT&T México, Movistar
**Ecuador:** Claro, Movistar, CNT
**Colombia:** Claro, Movistar, Tigo
**Spain:** Movistar, Vodafone, Orange
**United Kingdom:** EE, Vodafone, Three, O2

Carriers are not responsible for delayed or undelivered messages.`
        },
        {
          title: '10. Changes to This Policy',
          content: `We may update this SMS consent policy periodically:

• Changes will be posted on this page
• If we make significant changes, we will notify you by SMS or email
• Your continued use of our services after changes implies acceptance

We recommend reviewing this page periodically.`
        },
        {
          title: '11. Contact',
          content: `For any questions about this policy or our SMS practices:

**AJ Code Labs**
Email: contacto@ajcodelabs.ai

**SMS Responses:**
• STOP - Cancel all messages
• HELP - Get help
• INFO - Program information

Offices:
• Miami, FL - United States
• Quito - Ecuador
• London - United Kingdom`
        }
      ],
      quickActions: {
        title: 'Quick SMS Actions',
        actions: [
          { command: 'STOP', description: 'Cancel all SMS messages', type: 'cancel' },
          { command: 'HELP', description: 'Get help about the SMS program', type: 'help' },
          { command: 'INFO', description: 'Receive program information', type: 'info' },
        ]
      }
    }
  };

  const t = content[language];

  return (
    <main className="min-h-screen bg-[#050505]">
      <SEO 
        title={t.title}
        description={language === 'es' 
          ? 'Consentimiento para recibir mensajes SMS de AJ Code Labs. Gestione sus preferencias de comunicación.'
          : 'Consent to receive SMS messages from AJ Code Labs. Manage your communication preferences.'}
        url="/sms-consent"
      />
      
      <Navbar />

      {/* Hero Section */}
      <section className="pt-32 pb-16 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-[#F7931A]/5 to-transparent" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-[#F7931A] rounded-full blur-[300px] opacity-5" />
        
        <div className="container-custom relative z-10">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-[#A1A1AA] hover:text-[#F7931A] transition-colors mb-8 group"
            data-testid="back-home-link"
          >
            <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
            {t.backHome}
          </Link>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="flex items-center gap-4 mb-6"
          >
            <div className="p-3 glass rounded-xl">
              <MessageSquare className="w-8 h-8 text-[#F7931A]" />
            </div>
            <div>
              <h1 className="font-['Syne'] text-4xl md:text-5xl lg:text-6xl font-bold text-white">
                {t.title}
              </h1>
              <p className="text-[#A1A1AA] mt-2">{t.lastUpdated}</p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Quick Actions Card */}
      <section className="pb-8">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="max-w-4xl mx-auto"
          >
            <div className="glass rounded-2xl p-6 md:p-8 border border-[#F7931A]/20">
              <h2 className="font-['Syne'] text-xl font-bold text-white mb-4 flex items-center gap-2">
                <MessageSquare className="w-5 h-5 text-[#F7931A]" />
                {t.quickActions.title}
              </h2>
              <div className="grid md:grid-cols-3 gap-4">
                {t.quickActions.actions.map((action, index) => (
                  <div 
                    key={index}
                    className="flex items-center gap-3 p-4 bg-[#0A0A0A] rounded-xl"
                  >
                    {action.type === 'cancel' ? (
                      <XCircle className="w-6 h-6 text-red-500 flex-shrink-0" />
                    ) : (
                      <CheckCircle className="w-6 h-6 text-[#F7931A] flex-shrink-0" />
                    )}
                    <div>
                      <span className="font-mono text-[#F7931A] font-bold block">{action.command}</span>
                      <span className="text-[#A1A1AA] text-sm">{action.description}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Intro Section */}
      <section className="pb-8">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.15 }}
            className="max-w-4xl mx-auto"
          >
            <div className="glass rounded-2xl p-6 md:p-8">
              <h2 className="font-['Syne'] text-xl md:text-2xl font-bold text-white mb-3">
                {t.intro.title}
              </h2>
              <p className="text-[#A1A1AA] leading-relaxed">
                {t.intro.content}
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <section className="pb-24">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="max-w-4xl mx-auto"
          >
            <div className="glass rounded-2xl p-8 md:p-12">
              {t.sections.map((section, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.05 }}
                  className={index !== t.sections.length - 1 ? 'mb-10 pb-10 border-b border-[#27272A]' : ''}
                >
                  <h2 className="font-['Syne'] text-xl md:text-2xl font-bold text-white mb-4">
                    {section.title}
                  </h2>
                  <div className="text-[#A1A1AA] leading-relaxed whitespace-pre-line prose-invert">
                    {section.content.split('**').map((part, i) => 
                      i % 2 === 1 ? (
                        <strong key={i} className="text-white font-semibold">{part}</strong>
                      ) : (
                        <span key={i}>{part}</span>
                      )
                    )}
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </main>
  );
};

export default SmsConsentPage;
